import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ReferenceLine,
  Cell,
} from "recharts";

import {
  IsGroupSelected,
  OnGroupClick,
  TickFormatter,
} from "../../utils/ChartUtils";
import { BenchmarkLine } from "../../types/DashboardInterfaces";
import { isValidChartQuery } from "../../utils/DashboardUtils";
import useChartData from "./WidgetHooks/useChartData";
import ChartTooltip from "./Tooltips/ChartTooltip";
import { BaseChartProps } from "./WidgetTypes";
import useModule from "../../hooks/useModule";
import useThemeColors from "./useThemeColors";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

const emptyArr: [] = [];

const BarChartWidget = ({
  section,
  setSettings,
  record,
  theme,
  topLevelFilters,
  viewFilters,
  onSelectGroup,
  selectedGroup,
}: BaseChartProps) => {
  const {
    chartData,
    chartError,
    chartLoading,
    groupByField,
    groupByFieldName,
    groupByFieldType,
    aggregationField,
    aggregationMethod,
    aggregationFieldName,
    aggregationChartColor,
    benchmarkLines,
  } = useChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    validator: isValidChartQuery,
    orderBy: section?.OrderBy ?? null,
    record: record,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData });
  const { module } = useModule(section?.ModuleAPIName);

  const finalData = Array.isArray(chartData) ? chartData : emptyArr;

  const xAxis = groupByField;
  const yAxis = aggregationMethod;

  // --------------- No Records Found State ---------------
  if (chartError) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  // ------------------ Loading State ----------------------
  if (chartLoading) {
    return <WidgetLoading />;
  }

  const isHorizontal = section?.Orientation === "Horizontal";

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={finalData}
        layout={isHorizontal ? "vertical" : "horizontal"}
        margin={{
          top: 20,
          right: 15,
          left: 0,
          bottom: 0,
        }}
      >
        {/* <CartesianGrid strokeDasharray="3 3" /> */}
        <XAxis
          name={groupByField}
          dataKey={isHorizontal ? yAxis : xAxis}
          type={isHorizontal ? "number" : "category"}
          // TODO: Font size based on Widget size or number of groups
          fontSize={14}
          tickLine={false}
          axisLine={true}
          fontWeight={500}
          tickFormatter={(value) =>
            TickFormatter({
              value,
              groupByFieldName,
              groupByField,
              groupByFieldType,
              chartData,
            })
          }
          // label={{ fill: "black" }}
        />
        <YAxis
          dataKey={isHorizontal ? xAxis : yAxis}
          type={isHorizontal ? "category" : "number"}
          tick={{ fontSize: 14 }}
          tickLine={false}
          axisLine={true}
          fontWeight={500}
          // label={{ fill: "black" }}
        />
        <Tooltip
          cursor={{ fill: "var(--superlight)" }}
          content={
            <ChartTooltip
              aggregationField={aggregationField}
              aggregationMethod={aggregationMethod}
              aggregationFieldName={aggregationFieldName}
              groupByField={groupByField}
              groupByFieldName={groupByFieldName}
              moduleName={module?.Name ?? ""}
            />
          }
        />
        {/* <Legend /> */}
        <Bar
          dataKey={aggregationMethod}
          radius={[4, 4, 0, 0]}
          onClick={
            onSelectGroup
              ? (entry) => {
                  OnGroupClick({
                    groupByField: groupByField,
                    groupByFieldType: groupByFieldType,
                    entry: entry,
                    rawGroupByField: section?.Query?.GroupBy?.Field,
                    onSelectGroup: onSelectGroup,
                    filters: section?.Query?.Filters,
                  });
                }
              : undefined
          }
        >
          {Array.isArray(chartData) &&
            chartData.map((entry: any, i) => {
              const isSelected = IsGroupSelected({
                selectedGroup,
                groupValue: entry,
                groupByField,
                rawGroupByField: section?.Query?.GroupBy?.Field,
                groupByFieldType,
              });

              const fill = aggregationChartColor || colors?.[i];

              const finalFill =
                selectedGroup !== undefined ? `${fill}80` : fill;

              return (
                <Cell
                  key={`cell-${i}`}
                  cursor="pointer"
                  fill={isSelected ? fill : finalFill}
                  stroke={isSelected ? `${fill}80` : undefined}
                  strokeWidth={4}
                />
              );
            })}
          <LabelList fontWeight={600} position={"top"} dataKey={"readable"} />
        </Bar>
        {benchmarkLines?.map((benchmarkLine: BenchmarkLine, i: number) => {
          return (
            <ReferenceLine
              key={i}
              y={benchmarkLine.Value}
              label={{
                value: benchmarkLine.Label,
                fontSize: 14,
                fontWeight: 600,
                position: "top",
                fill: benchmarkLine.Color,
              }}
              stroke={benchmarkLine.Color}
              strokeDasharray="3 3"
            />
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BarChartWidget;
